import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useFilterContext } from './filtercontext';

// Tri-state filter for boolean fields. Lets the user choose between:
//   'all'   → no filter applied
//   'true'  → keep items where item[field] is truthy
//   'false' → keep items where item[field] is falsy
//
// Used for fields like `hasTransferPartner` where a user may want to show only
// internal transfers, exclude them entirely, or see everything.
export default function TriStateBooleanFilter({
  field,
  label = 'Filter',
  allLabel = 'All',
  trueLabel = 'Yes',
  falseLabel = 'No',
  defaultValue = 'all',
}) {
  const { registerFilter, clearFilter } = useFilterContext();
  const [value, setValue] = useState(defaultValue);

  const handleClearFromContext = useCallback(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (value === 'all') {
      clearFilter(field);
      return;
    }

    const wantsTrue = value === 'true';
    const filterFn = (item) => Boolean(item[field]) === wantsTrue;
    const descriptionFn = () => `${label}: ${wantsTrue ? trueLabel : falseLabel}`;
    registerFilter(field, label, filterFn, handleClearFromContext, descriptionFn);
  }, [value, field, label, trueLabel, falseLabel, handleClearFromContext, registerFilter, clearFilter]);

  const handleChange = (_event, next) => {
    if (next !== null) setValue(next);
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        size="small"
        onChange={handleChange}
        aria-label={label}
        fullWidth
      >
        <ToggleButton value="all" aria-label={allLabel}>{allLabel}</ToggleButton>
        <ToggleButton value="true" aria-label={trueLabel}>{trueLabel}</ToggleButton>
        <ToggleButton value="false" aria-label={falseLabel}>{falseLabel}</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
